import {gsap} from "gsap";
import {BS2Statics} from "../config/BS2Statics";
import {BS2} from "../BS2";
import {BS2Sound} from "../config/sound/BS2Sound";
import {SimpleReelModelSymbolData, SimpleSymbolState} from "../../core/reel/simplereel/SimpleReelModel";
import {GSAPUtils} from "../utils/GSAPUtils";
import {BS2GameMode} from "../server/BS2GameMode";
import {WildSymbol} from "../reels/WildSymbol";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {APIBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import {APIBetType} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetType";
import {SimpleSpinReelModel} from "../../core/reel/simplereel/SimpleSpinReelModel";
import {replaceWithNewSymbolDataType} from "../server/BS2Data";
import {BS2Symbol} from "../reels/BS2Symbol";
import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {BetFeature} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/bethandler/BetFeatureController";


export interface BS2APIBetEventData extends APIBetEventData {
    replayAndFeatureBuy?: boolean
}

/**
 * Created by Jerker Nord on 2021-01-28.
 */

export class BS2NormalBetPresentation implements FlowStatePresentation {
    private _stepIndex: number = 0;
    private _betDone: boolean = false;
    private _startDone: boolean = false;
    private _flowStateController?: FlowStateController;
    private _apiEventData: APIBetEventData;

    constructor() {
        BS2.apiPlugin.events.on(APIEvent.BET, (data: APIBetEventData) => this.onBet(data));
    }

    start(flowStateController: FlowStateController): void {
        const activeBetFeature: BetFeature | undefined = BS2.apiPlugin.betFeatureController.getActiveBetFeature();
        if (activeBetFeature?.name === BS2Statics.BOOSTED_BET_2) {
            BS2.soundPlayer.fadeOutLoopMusic(BS2Sound.SFS_INTRO_LOOP);
            BS2.soundPlayer.fadeOutMusic();
            BS2.soundPlayer.fadeInLoopMusic(BS2Sound.XBET2_LOOP, 0.7);
        } else if (activeBetFeature?.name === BS2Statics.BOOSTED_BET_1 && BS2.model.nextLevel < 2) {
            BS2.soundPlayer.fadeOutMusic();
            // BS2.soundPlayer.fadeInLoopMusic(BS2Sound.RITUAL_LOOP_1,1);
        } else {
            BS2.soundPlayer.switchMusic(BS2.model.data.nextMode);
        }
        this._flowStateController = flowStateController;
        this._startDone = true;
        this.tryRunStart(this._flowStateController);
    }

    public makeBetDoneAndStartDoneFalse(): void {
        this._betDone = false;
        this._startDone = false;
    }

    public animate(symbols: (SimpleReelModelSymbolData | null)[], dropDuration: number, delay: number, state: SimpleSymbolState, reverse: boolean, shouldPlayWildLandSound: boolean = false): gsap.core.Timeline {
        let drops: gsap.core.Timeline[] = [];

        let cntr: number = 0;
        let firstSound: boolean = true;
        for (let j: number = 0; j < symbols.length; j++) {
            const symbol: SimpleReelModelSymbolData | null = symbols[j];
            if (symbol == undefined) {
                continue;
            }

            const stepDiff: number = j - symbol.reelPosition;
            symbol.reelPosition = j;

            const windUp: gsap.core.Tween = gsap.to(symbol.positionable, {
                duration: dropDuration * 8,
                y: symbol.positionable.position.y - (BS2Statics.stepSize.y * 0.4),
                ease: "power2.inOut"
            });
            const windBack: gsap.core.Tween = gsap.to(symbol.positionable, {
                duration: dropDuration * 8,
                y: symbol.positionable.position.y,
                ease: "power2.in"
            });

            const move: gsap.core.Tween = gsap.to(symbol.positionable, {
                duration: dropDuration * stepDiff,
                y: symbol.positionable.position.y + BS2Statics.stepSize.y * stepDiff,
                ease: "none"
            });

            const dropTl: gsap.core.Timeline = gsap.timeline();
            dropTl.add(windUp);
            dropTl.add(windBack, ">0.03");
            dropTl.add(move);
            dropTl.add((<WildSymbol>symbol.positionable).getStickyAnimation(symbols[j]?.reelId, (cntr === 0)));
            if (firstSound && shouldPlayWildLandSound) {
                firstSound = false;
                dropTl.add(() => {
                    BS2.soundPlayer.playWildLandingSound(symbol.reelId);
                })
            }
            drops.push(dropTl);
            cntr++;
        }


        if (reverse) {
            drops = drops.reverse();
        }

        return GSAPUtils.createTimelineFromArray(drops, `<${delay}`);
    }

    //Turn off boosted bet on feature buy
    private onBet(data: APIBetEventData): void {
        this._apiEventData = data;
        const BS2Data: BS2APIBetEventData = <BS2APIBetEventData>data;

        // if(BS2.model.gameData){
        //     this.replaceWildWithBlankSym(); //this is used to ensure no overlapping of wild on bottom row
        // }

        if (data.type == APIBetType.FEATURE_BET || BS2Data.replayAndFeatureBuy) {
            if (data.playerInteraction?.featureName === BS2Statics.BOOSTED_BET_2) {
                BS2.reelView.setReeSet(BS2Statics.LEVEL_REEL_SETS[5]);
            } else if (data.playerInteraction?.featureName === BS2Statics.BOOSTED_BET_1) {
                BS2.reelView.setReeSet(BS2Statics.LEVEL_REEL_SETS[0]);
            } else {
                if (data.type !== APIBetType.FEATURE_BET) {
                    BS2.reelView.setReeSet(BS2Statics.LEVEL_REEL_SETS[0]);
                } else {
                    BS2.reelView.setReeSet(BS2Statics.FEATURE_BET_REEL_SET);
                }
            }
        } else {
            if (BS2.model.data.nextMode == BS2GameMode.NORMAL) {
                BS2.reelView.setReeSet(BS2Statics.LEVEL_REEL_SETS[0]);
            }
        }


        if (BS2.model.data.nextMode === BS2GameMode.NORMAL) {
            if (data.playerInteraction?.featureName === BS2Statics.BOOSTED_BET_1) {
                this.openCloseStickyWildForXBetOne(data);
            } else if (data.playerInteraction?.featureName === BS2Statics.BOOSTED_BET_2) {
                this.openCloseStickyWildForXBetTwo(data);
            } else {
                for (let reelIdx: number = 0; reelIdx < BS2Statics.NR_OF_REELS; reelIdx++) {
                    if (!BS2.reelView.bottomStickyWildView.isVisible(reelIdx)) {
                        BS2.reelView.bottomStickyWildView.showStickyWild(reelIdx, 0.25, false);
                    }
                    if (!BS2.reelView.bottomStickyWildView.isLocked(reelIdx)) {
                        BS2.reelView.bottomStickyWildView.lockStickyWildTree(reelIdx, 0.25, false);
                    }
                }
            }

        }

        this._betDone = true;
        this.tryRunStart(this._flowStateController);
    }

    private openCloseStickyWildForXBetTwo(data: APIBetEventData) {
        let shouldPlayWildOpeningSound: boolean = !BS2.model.isRestore;
        for (let reelIdx: number = 0; reelIdx < BS2Statics.NR_OF_REELS; reelIdx++) {
            if (data.playerInteraction?.featureName === BS2Statics.BOOSTED_BET_2 && BS2.reelView.bottomStickyWildView.isVisible(reelIdx)) {
                BS2.reelView.bottomStickyWildView.removeStickyWild(reelIdx, shouldPlayWildOpeningSound);
                shouldPlayWildOpeningSound = false;
            }
        }
        BS2.backgroundElements.switchLevel(5, true, false, true);
    }

    private openCloseStickyWildForXBetOne(data: APIBetEventData): void {
        let shouldPlayWildOpeningSound: boolean = !BS2.model.isRestore;
        for (let reelIdx: number = 0; reelIdx < BS2Statics.NR_OF_REELS; reelIdx++) {
            if (data.playerInteraction?.featureName === BS2Statics.BOOSTED_BET_1 && reelIdx === 0) {
                if (!BS2.reelView.bottomStickyWildView.isVisible(reelIdx) && BS2.reelView.reels[0].model.getVisibleSymbolAt(4).name !== "W") {
                    BS2.reelView.bottomStickyWildView.showStickyWild(reelIdx, 0.25, false).progress(1);
                }
                if (BS2.reelView.bottomStickyWildView.isLocked(reelIdx)) {
                    BS2.reelView.bottomStickyWildView.unLockStickyWildTree(reelIdx, shouldPlayWildOpeningSound, 0.25, false, true);
                    shouldPlayWildOpeningSound = false;
                    const replaceData = BS2.reelView.reels[0].model.replaceWithNewSymbol(BS2Statics.BOT_STICKY_WILD_IDX_WITH_BUFFER, BS2Statics.SYMBOLS.W);
                    (<WildSymbol>replaceData.newSymbol.positionable).getStickyAnimation();
                    (<BS2Symbol>replaceData.oldSymbol.positionable).destroy();
                    // console.log(`replace in openCloseNB ${replaceData.oldSymbol.name}-->${replaceData.newSymbol.name}`)
                }
                if ((<BS2Symbol>BS2.reelView.reels[0].model.getAllVisibleSymbols()[BS2Statics.BOT_STICKY_WILD_IDX_VIS_AREA].positionable).currMultiValue > 1) {
                    (<BS2Symbol>BS2.reelView.reels[0].model.getAllVisibleSymbols()[BS2Statics.BOT_STICKY_WILD_IDX_VIS_AREA].positionable).resetMultiplier();
                }
                BS2.backgroundElements.switchLevel(1);
            } else {
                if (!BS2.reelView.bottomStickyWildView.isVisible(reelIdx)) {
                    BS2.reelView.bottomStickyWildView.showStickyWild(reelIdx, 0.25, false);
                }
                if (!BS2.reelView.bottomStickyWildView.isLocked(reelIdx)) {
                    BS2.reelView.bottomStickyWildView.lockStickyWildTree(reelIdx, 0.25, false);
                }
            }
        }
    }

    private tryRunStart(_flowStateController?: FlowStateController) {
        if (_flowStateController && this._startDone && this._betDone) {
            this.runStart(_flowStateController);
            this._startDone = false;
            this._betDone = false;
        }
    }

    private runStart(_flowStateController: FlowStateController): void {
        const tl: gsap.core.Timeline = gsap.timeline(),
            stickyDrop: gsap.core.Timeline[] = [],
            isXBetOneBetData: boolean = this._apiEventData?.playerInteraction?.featureName === BS2Statics.BOOSTED_BET_1;
        if (isXBetOneBetData) {
            const reelModel: SimpleSpinReelModel = BS2.reelView.reels[0].model;
            if (reelModel.getVisibleSymbolAt(BS2Statics.BOT_STICKY_WILD_IDX_VIS_AREA).name === BS2Statics.SYMBOLS.W) {
                const replaceData: replaceWithNewSymbolDataType = BS2.reelView.reels[0].model.replaceWithNewSymbol(reelModel.visibleIndexToRealIndex(BS2Statics.BOT_STICKY_WILD_IDX_VIS_AREA), "X"),
                    wildSymbol: WildSymbol = <WildSymbol>replaceData.oldSymbol.positionable;
                wildSymbol.moveToWildLocked();
                wildSymbol.setSticky(true);
                BS2.reelView.stickySymbols.setValueForCoordinates([{
                    column: 0,
                    row: BS2Statics.BOT_STICKY_WILD_IDX_VIS_AREA
                }], replaceData.oldSymbol);
                // console.log(`replace in runStartNB ${replaceData.oldSymbol.name}-->${replaceData.newSymbol.name}`)
                const tl: gsap.core.Timeline = this.animate([BS2.reelView.stickySymbols.matrix[0][BS2Statics.BOT_STICKY_WILD_IDX_VIS_AREA]], BS2Statics.stepDuration, 0, SimpleSymbolState.COLLAPSE_L2R, false);
                stickyDrop.push(tl);
            }
        }
        if (BS2.model.isNextFreeSpin() || BS2.model.isNextSuperFreespin()) {
            BS2Sound.WILD_STICKY.reset();
            let shouldPlayWildStickySound: boolean = false;

            for (let reelIdx: number = 0; reelIdx < BS2.reelView.reels.length; reelIdx++) {
                const reelModel: SimpleSpinReelModel = BS2.reelView.reels[reelIdx].model,
                    symbolsData: SimpleReelModelSymbolData[] = reelModel.getAllVisibleSymbols(),
                    conditionLength: number = this.calculateConditionLengthForStickyWild(reelIdx, symbolsData);
                for (let symIdx: number = 0; symIdx < conditionLength; symIdx++) {
                    const symbol: SimpleReelModelSymbolData = symbolsData[symIdx];
                    if (symbol.name === BS2Statics.SYMBOLS.W) {
                        const replaceData: replaceWithNewSymbolDataType = BS2.reelView.reels[reelIdx].model.replaceWithNewSymbol(reelModel.visibleIndexToRealIndex(symbol.reelPosition), "X"),
                            wildSymbol: WildSymbol = <WildSymbol>replaceData.oldSymbol.positionable;
                        shouldPlayWildStickySound = true;
                        wildSymbol.moveToWildLocked();
                        wildSymbol.setSticky(true);
                        BS2.reelView.stickySymbols.setValueForCoordinates([{
                            column: symbol.reelId,
                            row: symbol.reelPosition
                        }], replaceData.oldSymbol);
                    }
                }
            }
            if (shouldPlayWildStickySound) {
                tl.add(() => BS2.soundPlayer.playSound(BS2Sound.WILD_STICKY.next()));
            }

            //Collapse stickys
            let shouldPlayWildLandSound: boolean = true;
            for (let reelIdx: number = 0; reelIdx < BS2.reelView.stickySymbols.matrix.length; reelIdx++) {
                const localPop: (SimpleReelModelSymbolData | null)[] = [],
                    reelHeight: number = this.calculateConditionLengthForStickyWild(reelIdx, BS2.reelView.reels[reelIdx].model.getAllVisibleSymbols());
                for (let symIdx: number = reelHeight - 1; symIdx >= 0; symIdx--) {
                    const symbol: SimpleReelModelSymbolData | null = BS2.reelView.stickySymbols.matrix[reelIdx][symIdx];
                    if (symbol === null) {
                        const spliced: (SimpleReelModelSymbolData | null)[] = BS2.reelView.stickySymbols.matrix[reelIdx].splice(symIdx, 1);
                        localPop.unshift(spliced[0]);
                    }
                }

                for (let sym of localPop) {
                    BS2.reelView.stickySymbols.matrix[reelIdx].unshift(sym);
                }

                const tl: gsap.core.Timeline = this.animate(BS2.reelView.stickySymbols.matrix[reelIdx], BS2Statics.stepDuration, 0, SimpleSymbolState.COLLAPSE_L2R, false, shouldPlayWildLandSound);
                for (let symIdx: number = reelHeight - 1; symIdx >= 0; symIdx--) {
                    const symbol: SimpleReelModelSymbolData | null = BS2.reelView.stickySymbols.matrix[reelIdx][symIdx];
                    if (symbol !== null) {
                        shouldPlayWildLandSound = false;
                    }
                }
                stickyDrop.push(tl);
            }
        }

        const stepSizes = [
            {x: BS2Statics.stepSize.x, y: BS2Statics.stepSize.y}
        ]

        const spinTl: gsap.core.Timeline = gsap.timeline();

        for (let i: number = 0; i < BS2.reelView.reels.length; i++) {
            if (BS2.reelView.reels[i].model.active) {
                const reelStart: gsap.core.Timeline = gsap.timeline();
                reelStart.add(BS2.reelView.reels[i].model.start(false));
                if (stickyDrop[i] != undefined) {
                    reelStart.add(stickyDrop[i], 0);
                }
                spinTl.add(reelStart, "<0.01");
            }
        }
        this._stepIndex++;
        this._stepIndex %= stepSizes.length;


        tl.add(() => {
            BS2.soundPlayer.playSound(BS2Sound.REEL_START);
        });
        tl.add([spinTl]);
        tl.add(this.resetLevel(), "<");
        tl.add(() => {
            BS2.soundPlayer.playSound(BS2Sound.REEL_SPINNING);
            BS2.reelView.hideAllRemainingScatters();
        }, "<");
        tl.add(() => {
            _flowStateController.finish();
            this._flowStateController = undefined;
        });
    }

    private calculateConditionLengthForStickyWild(reelIdx: number, symbolsData: SimpleReelModelSymbolData[]): number {
        if (BS2.reelView.bottomStickyWildView.isVisible(reelIdx)) {
            return symbolsData.length - 1;
        }
        return symbolsData.length;
    }

    private resetLevel(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (BS2.model.data.nextMode === BS2GameMode.NORMAL) {
            tl.add(() => {
                BS2.reelView.collectBar.resetToZero(true, BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_1, false, true);
                for (let reelIdx: number = 0; reelIdx < BS2Statics.NR_OF_REELS; reelIdx++) {
                    BS2.reelView.bottomStickyWildView.resetStickyWildFireAnimOnBet(reelIdx);
                }
            }, 0);

        }
        if (BS2.model.data.nextMode === BS2GameMode.NORMAL) {
            if (this._apiEventData?.playerInteraction?.featureName !== BS2Statics.BOOSTED_BET_2 && this._apiEventData?.playerInteraction?.featureName !== BS2Statics.BOOSTED_BET_1) {
                tl.add(BS2.backgroundElements.switchLevel(0), 0);
            }
        }

        return tl;
    }

    get apiEventData(): APIBetEventData {
        return this._apiEventData;
    }
}